import { Question } from "resources/question/view/types";
import {
  ArrayInput,
  DateInput,
  FileField,
  FileInput, Form, ReferenceInput,
  required, SelectInput, SimpleFormIterator,
  TextInput, useCreate, useNotify,
  useRefresh, useUpdate
} from "react-admin";
import * as React from "react";
import Modal from "components/Modal";
import { Box, Button, Stack, Typography } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { apiUrl, httpClient } from "config/dataProvider";
import {FILE_TYPE} from "components/material/view/info/FileAttachmentForm/types";

async function uploadFile(file: File): Promise<string | null> {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileType', FILE_TYPE.REGULAR);
    const response = await httpClient(`${apiUrl}file`, {
      method: 'POST',
      body: formData
    })
    return response.json.id
  } catch (error) {
    return null;
  }
}

export type AddQuestionDocumentModalProps = {
  isOpen: boolean;
  onClose: () => void;
  question: Question;
  document?: any
};

const AddQuestionDocumentModal = ({ isOpen, onClose, question, document }: AddQuestionDocumentModalProps) => {
  const refresh = useRefresh();
  const notify = useNotify();

  const isEdit = !!document?.id;

  const [create, { isPending: isCreating }] = useCreate();
  const [update, { isPending: isUpdating }] = useUpdate();
  const isSaving = isCreating || isUpdating;

  const defaultValues = React.useMemo(() => {
    if (!document) return { title: '', publishedAt: undefined, file: undefined };

    return {
      title: document.title ?? "",
      publishedAt: document.publishedAt ?? undefined,
      file: document.file.url
        ? { src: document.file.url, title: document.file.name ?? 'Файл' }
        : undefined,
    };
  }, [document]);

  const handleSave = async (data: any) => {
    try {
      const fileValue = data.file;
      const rawFile = fileValue?.rawFile;
      const fileId = !rawFile && isEdit ? document!.file.id : await uploadFile(rawFile)

      if (!fileId) {
        notify('Ошибка загрузки файла', { type: 'error' });
        return;
      }

      const payload = {
        title: data.title,
        publishedAt: data.publishedAt,
        fileId: fileId,
        questionId: question.id,
        typeId: data.typeId,
        audiences: data.audiences
      };

      if (isEdit) {
        await update(
          `question-document/${document!.id}`,
          { id: document!.id, data: payload },
          {
            onSuccess: () => {
              notify("Документ вопроса обновлен", { type: "success" });
              refresh();
              onClose();
            },
            onError: () => {
              notify("Ошибка при обновление документа вопроса", { type: "error" });
            },
          }
        );
      } else {
        await create(
          "question-document",
          { data: payload },
          {
            onSuccess: () => {
              notify("Документ вопроса добавлен", { type: "success" })
              refresh();
              onClose();
            },
            onError: () => {
              notify("Ошибка при создании документа вопроса", { type: "error" });
            },
          }
        )
      }
    } catch (e: any) {
      notify(e?.message ?? 'Ошибка', { type: 'error' });
    }
  }

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Form onSubmit={handleSave} defaultValues={defaultValues}>
        <Stack spacing={2}>
          <Typography variant="h6" mb={2}>
            {isEdit ? "Редактировать документ вопроса" : "Добавить документ вопроса"}
          </Typography>

          <TextInput
            fullWidth
            source="title"
            label="Название"
            size={'small'}
            validate={required()}
          />
          <DateInput
            fullWidth
            source="publishedAt"
            label="Дата публикации"
            validate={required()}
          />
          <ReferenceInput source="typeId" reference="question-document-type">
            <SelectInput optionText="title" optionValue="id" label="Тип" fullWidth validate={required()} size="small"/>
          </ReferenceInput>
          <ArrayInput source="audiences" label="Аудитория" validate={required()}>
            <SimpleFormIterator inline>
              <TextInput source={""} label={false} size="small" validate={required()}/>
            </SimpleFormIterator>
          </ArrayInput>
          <FileInput
            fullWidth
            source="file"
            label="Файл"
            validate={required()}
            multiple={false}
          >
            <FileField
              source="src"
              title="title"
              sx={{ wordBreak: 'break-word' }}
            />
          </FileInput>

          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button onClick={onClose}>Отмена</Button>
            <LoadingButton
              loading={isSaving}
              variant="contained"
              color="primary"
              sx={{ ml: 2 }}
              type="submit">
              {isEdit ? "Сохранить" : "Добавить"}
            </LoadingButton>
          </Box>
        </Stack>
      </Form>
    </Modal>
  )
}

export default AddQuestionDocumentModal;
